import { Alert, Box, Divider, Drawer, ListItemIcon, Menu, MenuItem, Snackbar, Stack, Typography } from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import SpeedOutlinedIcon from '@mui/icons-material/SpeedOutlined'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined'
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined'
import FormatListBulletedOutlinedIcon from '@mui/icons-material/FormatListBulletedOutlined'
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined'
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined'
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined'
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined'
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined'
import SwapHorizOutlinedIcon from '@mui/icons-material/SwapHorizOutlined'
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined'
import SyncAltOutlinedIcon from '@mui/icons-material/SyncAltOutlined'
import UnfoldMoreOutlinedIcon from '@mui/icons-material/UnfoldMoreOutlined'
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined'
import type { SvgIconComponent } from '@mui/icons-material'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useTeam } from '@/hooks/useTeam'
import { supportMailto } from '@/config/contact'
import { useEdition } from '@/hooks/useEdition'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { ENV_NAME, IS_NON_PROD } from '@/config/environment'
import SygnetCluster from './SygnetCluster'

export type SidebarItemId = 'dashboard' | 'studies' | 'activity' | 'live' | 'plans' | 'templates' | 'respondents' | 'team'

type NavItem = {
  id: SidebarItemId
  Icon: SvgIconComponent
  to?: string
  count?: number
  badge?: 'newBadge'
  /** Hidden unless the signed-in account belongs to a team. */
  teamOnly?: boolean
  /** Part of the research workshop — absent in the single-measurement edition. */
  proOnly?: boolean
}

const NAV_ITEMS: NavItem[] = [
  { id: 'dashboard', Icon: SpeedOutlinedIcon, to: '/dashboard' },
  { id: 'studies', Icon: DescriptionOutlinedIcon, to: '/projects' },
  // The workshop of a research programme: an activity stream across many
  // studies, a reusable question library, an address book of respondents.
  // Somebody running one measurement has nothing to put in any of them.
  { id: 'activity', Icon: HistoryOutlinedIcon, to: '/activity', proOnly: true },
  // The model demo stays in both editions — it is the only place anyone sees
  // how this works before they have paid for anything.
  { id: 'live', Icon: AutoAwesomeOutlinedIcon, to: '/camera' },
  // Not behind the avatar: every other route to the plans opens because
  // something ran out — a spent package, an ended trial. This is the one that
  // does not, so it belongs where somebody can find it before they are blocked.
  // Both editions get it: FaceWise upgrades from here, Pro checks what it pays for.
  { id: 'plans', Icon: LocalOfferOutlinedIcon, to: '/plans' },
  { id: 'templates', Icon: FormatListBulletedOutlinedIcon, to: '/questionnaires', proOnly: true },
  { id: 'respondents', Icon: GroupOutlinedIcon, to: '/respondents', proOnly: true },
  // Only rendered for accounts that belong to a team — a solo researcher has
  // no sublicences to manage and would just see an empty page.
  { id: 'team', Icon: GroupsOutlinedIcon, to: '/team', teamOnly: true },
]

export const SIDEBAR_WIDTH = 232

type ResearcherSidebarProps = {
  active?: SidebarItemId
  /** Controls the temporary mobile drawer (below the `md` breakpoint). */
  mobileOpen?: boolean
  onMobileClose?: () => void
}

export default function ResearcherSidebar({ active, mobileOpen = false, onMobileClose }: ResearcherSidebarProps) {
  const theme = useTheme()
  const c = theme.affexy.colors

  return (
    <>
      {/* Permanent sidebar — desktop / tablet (md and up) */}
      <Box
        component="aside"
        sx={{
          display: { xs: 'none', md: 'flex' },
          width: SIDEBAR_WIDTH,
          flex: `0 0 ${SIDEBAR_WIDTH}px`,
          bgcolor: c.navy800,
          color: '#fff',
          flexDirection: 'column',
          py: 2.5,
          height: '100dvh',
          position: 'sticky',
          top: 0,
        }}
      >
        <SidebarContent active={active} />
      </Box>

      {/* Temporary drawer — mobile (below md) */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: SIDEBAR_WIDTH,
            bgcolor: c.navy800,
            color: '#fff',
            py: 2.5,
            border: 'none',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <SidebarContent active={active} onNavigate={onMobileClose} />
      </Drawer>
    </>
  )
}

function SidebarContent({ active, onNavigate }: { active?: SidebarItemId; onNavigate?: () => void }) {
  const { inTeam } = useTeam()
  const { isBasic } = useEdition()
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('dashboard')
  const navigate = useNavigate()
  const { user, logout, switchView, switchEdition } = useAuth()
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)
  const [switchFailed, setSwitchFailed] = useState(false)

  const go = (to: string) => {
    navigate(to)
    onNavigate?.()
  }

  const fullName =
    user?.first_name && user?.last_name ? `${user.first_name} ${user.last_name}` : (user?.email ?? '')
  const initials = (user?.first_name?.[0] ?? user?.email?.[0] ?? '?').toUpperCase()
  // This sidebar only renders in the researcher view — the label doubles as
  // the "which view am I in" indicator.
  const roleLabel = t('nav.roleResearcher')

  const closeMenu = () => setMenuAnchor(null)

  const handleProfile = () => {
    closeMenu()
    navigate('/profile')
  }

  const handleSwitchToRespondent = () => {
    closeMenu()
    switchView('respondent')
    navigate('/')
  }

  const handleAdminPanel = () => {
    closeMenu()
    navigate('/admin')
  }

  /**
   * Read the application as the other edition. Admin-only, and a preview rather
   * than an upgrade: nothing about the account's plan, package or existing
   * studies moves — only which screens open.
   *
   * Lands on the dashboard afterwards on purpose. Half the screens fetched what
   * they show under the old edition, and some of them (the activity feed, the
   * respondent base) do not exist in the other one; starting over from the home
   * screen is cheaper and less confusing than deciding which of them to refetch
   * and which to bounce out of.
   */
  const handleSwitchEdition = async () => {
    closeMenu()
    try {
      await switchEdition(isBasic ? 'pro' : 'basic')
      navigate('/dashboard')
    } catch (error) {
      console.warn('Failed to switch edition:', error)
      setSwitchFailed(true)
    }
  }

  const handleLogout = async () => {
    closeMenu()
    await logout()
    navigate('/login')
  }

  return (
    <>
      {/* Brand lockup */}
      <Box sx={{ px: 2.25, pb: 2.5, display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: '8px',
            overflow: 'hidden',
            bgcolor: c.navy600,
            display: 'grid',
            placeItems: 'center',
            p: '3px',
          }}
        >
          <SygnetCluster />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.875, lineHeight: 1 }}>
          <Typography sx={{ fontFamily: theme.affexy.fonts.heading, fontWeight: 700, fontSize: 19, letterSpacing: '-0.01em' }}>
            FaceWise
          </Typography>
          {/* The wordmark is "FaceWise" in both editions; only Pro wears the
              mark after it. Non-prod deployments show the environment name
              instead — "DEV" — so the build is unmistakable at a glance, and
              that stays useful regardless of which edition is signed in. */}
          {(IS_NON_PROD || !isBasic) && (
            <Box
              component="span"
              title={IS_NON_PROD ? `${ENV_NAME} environment` : undefined}
              sx={{
                fontSize: 8.5,
                fontWeight: 700,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: c.coral,
                bgcolor: alpha(c.coral, 0.16),
                px: 0.625,
                py: 0.25,
                borderRadius: '5px',
                transform: 'translateY(-2px)',
              }}
            >
              {IS_NON_PROD ? ENV_NAME : t('nav.proBadge')}
            </Box>
          )}
        </Box>
      </Box>

      {/* Nav */}
      <Stack spacing={0.25} sx={{ px: 1.5, flex: 1 }}>
        {NAV_ITEMS.filter(
          (item) => (!item.teamOnly || inTeam) && (!item.proOnly || !isBasic),
        ).map((item) => {
          const isActive = item.id === active
          const { Icon } = item
          return (
            <Box
              key={item.id}
              component="button"
              onClick={() => item.to && go(item.to)}
              sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 1.5,
                py: 1.25,
                border: 'none',
                borderRadius: '12px',
                cursor: 'pointer',
                textAlign: 'left',
                bgcolor: isActive ? alpha(c.coral, 0.12) : 'transparent',
                color: isActive ? '#fff' : 'rgba(255,255,255,.66)',
                fontFamily: theme.affexy.fonts.body,
                fontSize: 14,
                fontWeight: isActive ? 500 : 400,
                transition: 'background-color 120ms',
                '&:hover': { bgcolor: isActive ? alpha(c.coral, 0.12) : 'rgba(255,255,255,.06)' },
              }}
            >
              {isActive && (
                <Box
                  sx={{
                    position: 'absolute',
                    left: -12,
                    top: 8,
                    bottom: 8,
                    width: 3,
                    bgcolor: c.coral,
                    borderRadius: 34,
                  }}
                />
              )}
              <Icon sx={{ fontSize: 18, color: isActive ? c.coral : 'currentColor' }} />
              <Box component="span" sx={{ flex: 1 }}>
                {t(`nav.${item.id}`)}
              </Box>
              {item.badge && (
                <Box
                  component="span"
                  sx={{
                    fontSize: 9,
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    px: 0.75,
                    py: 0.25,
                    borderRadius: 34,
                    bgcolor: c.coral,
                    color: '#fff',
                  }}
                >
                  {t(`nav.${item.badge}`)}
                </Box>
              )}
              {item.count != null && (
                <Box
                  component="span"
                  sx={{
                    fontSize: 11,
                    px: 0.875,
                    py: 0.25,
                    borderRadius: 34,
                    bgcolor: 'rgba(255,255,255,.08)',
                    color: 'rgba(255,255,255,.6)',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {item.count}
                </Box>
              )}
            </Box>
          )
        })}
      </Stack>

      {/* Footer: user menu trigger */}
      <Box sx={{ px: 1.5 }}>
        <Box
          component="button"
          onClick={(e: React.MouseEvent<HTMLElement>) => setMenuAnchor(e.currentTarget)}
          aria-haspopup="true"
          aria-expanded={Boolean(menuAnchor)}
          sx={{
            width: '100%',
            mt: 0.75,
            pt: 1.75,
            px: 1.5,
            pb: 0.5,
            border: 'none',
            borderTop: '1px solid rgba(255,255,255,.08)',
            bgcolor: 'transparent',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            borderRadius: '12px',
            textAlign: 'left',
            transition: 'background-color 120ms',
            '&:hover': { bgcolor: 'rgba(255,255,255,.06)' },
          }}
        >
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 999,
              background: theme.affexy.gradients.coral,
              color: '#fff',
              display: 'grid',
              placeItems: 'center',
              fontFamily: theme.affexy.fonts.heading,
              fontWeight: 700,
              fontSize: 12,
              flexShrink: 0,
            }}
          >
            {initials}
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontSize: 13, lineHeight: 1.2, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {fullName}
            </Typography>
            <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,.48)' }}>{roleLabel}</Typography>
          </Box>
          <UnfoldMoreOutlinedIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,.48)', flexShrink: 0 }} />
        </Box>

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={closeMenu}
          anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          slotProps={{ paper: { sx: { minWidth: 200, borderRadius: '12px', mt: -0.5 } } }}
        >
          <MenuItem onClick={handleProfile}>
            <ListItemIcon>
              <PersonOutlineOutlinedIcon fontSize="small" />
            </ListItemIcon>
            {t('nav.profile')}
          </MenuItem>
          <MenuItem onClick={handleSwitchToRespondent}>
            <ListItemIcon>
              <SwapHorizOutlinedIcon fontSize="small" />
            </ListItemIcon>
            {t('nav.switchToRespondent')}
          </MenuItem>
          {user?.can_view_admin_panel && (
            <MenuItem onClick={handleAdminPanel}>
              <ListItemIcon>
                <AdminPanelSettingsOutlinedIcon fontSize="small" />
              </ListItemIcon>
              {t('nav.adminPanel')}
            </MenuItem>
          )}
          {/* Switch the account between FaceWise and FaceWise Pro. Only for the
              people who own the product: comparing the editions used to mean two
              accounts and two logins. Hidden on a team seat, where it would do
              nothing visible — a seat on a team is Pro whatever the account's own
              column says (licensing_app.access.edition_of). */}
          {user?.can_view_admin_panel && !inTeam && (
            <MenuItem onClick={() => void handleSwitchEdition()}>
              <ListItemIcon>
                <SyncAltOutlinedIcon fontSize="small" />
              </ListItemIcon>
              {isBasic ? t('nav.switchToPro') : t('nav.switchToBasic')}
            </MenuItem>
          )}
          <MenuItem
            component="a"
            href={supportMailto(t('nav.helpMailSubject'))}
            onClick={closeMenu}
          >
            <ListItemIcon>
              <HelpOutlineOutlinedIcon fontSize="small" />
            </ListItemIcon>
            {t('nav.help')}
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <LogoutOutlinedIcon fontSize="small" />
            </ListItemIcon>
            {t('nav.logout')}
          </MenuItem>
        </Menu>
      </Box>

      <Snackbar
        open={switchFailed}
        autoHideDuration={6000}
        onClose={() => setSwitchFailed(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setSwitchFailed(false)} sx={{ width: '100%' }}>
          {t('nav.switchEditionFailed')}
        </Alert>
      </Snackbar>
    </>
  )
}
